import { Button, Field, Icon, Input } from '@dayon/design-system';

/**
 * Enrolling an authenticator, mid-sign-in.
 *
 * Reached from the ENROL challenge the sign-in step issues to an admin whose
 * role mandates a second factor and who has none — which today means a Super
 * Admin has just reset their authenticator. The password step has already
 * passed for exactly this account.
 *
 * NO CREDENTIAL WAS TRANSMITTED TO GET HERE. The secret below was generated on
 * the server and is shown to the person at the keyboard, on their own screen,
 * once — which is the only safe way an authenticator secret ever moves. Nobody
 * emailed it, nobody read it out over the phone, and the admin who reset the
 * factor never saw it.
 *
 * WHY THERE IS NO QR CODE. Rendering one means a barcode encoder, and this
 * platform adds no dependency it does not need. The `otpauth://` link below is
 * the same payload a QR encodes: on a phone, tapping it opens the authenticator
 * app directly — which is better than a QR on the device you are already
 * holding — and on a desktop the secret underneath it is typed into the app by
 * hand, which every authenticator supports.
 */
export function TwoFactorEnrolment({
  email,
  uri,
  secret,
  code,
  onCode,
  busy,
  starting,
  inlineError,
  seconds,
  onSubmit,
  onRestart,
  recoveryCodes,
}: {
  email: string;
  uri: string | null;
  secret: string | null;
  code: string;
  onCode: (v: string) => void;
  busy: boolean;
  starting: boolean;
  inlineError: string | null;
  seconds: number;
  onSubmit: (e: React.FormEvent) => void;
  onRestart: () => void;
  recoveryCodes: string[] | null;
}) {
  if (recoveryCodes) {
    return (
      <div className="stack signin-step">
        <div className="signin-check">
          <Icon name="shield-check" />
        </div>
        <div>
          <h3>Authenticator enrolled</h3>
          <div className="text-subtle signin-sub">
            Save these recovery codes before you go any further.
          </div>
        </div>

        <div className="notice" role="alert">
          <Icon name="warning-circle" />
          <div>
            <strong>Shown once.</strong> The platform stores only their digests, so nobody — not a
            Super Admin, not this screen — can show them to you again. Each one works exactly once,
            in place of a code from your app. Lose both these and your phone and the only way back
            is another factor reset.
          </div>
        </div>

        <ul className="secret-codes">
          {recoveryCodes.map((c) => (
            <li key={c}>
              <code>{c}</code>
            </li>
          ))}
        </ul>

        <a className="btn btn-primary btn-block" href="/">
          Open the console
          <Icon name="arrow-right" />
        </a>
      </div>
    );
  }

  return (
    <form className="stack signin-step" onSubmit={onSubmit} noValidate>
      <div>
        <h3>Enrol an authenticator</h3>
        <div className="text-subtle signin-sub">
          Your role needs a second factor and this account has none, so {email} cannot be signed in
          until one is enrolled.
        </div>
      </div>

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          This is where a <strong>2FA reset</strong> leaves you. Nothing was sent to you and
          nothing was told to the admin who reset it — the secret below was generated for this
          screen and is shown only here. If you did not expect this, stop and tell a Super Admin:
          somebody cleared your second factor, and the trail records who and when.
        </div>
      </div>

      {starting || !uri || !secret ? (
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      ) : (
        <>
          <Field
            label="Open in your authenticator app"
            hint="On a phone, this opens the app directly. There is no QR code: drawing one would mean adding a barcode library, and this link carries exactly what a QR would encode."
          >
            {({ id }) => (
              <a id={id} className="secret-block" href={uri}>
                {uri}
              </a>
            )}
          </Field>

          <Field
            label="Or type this secret in by hand"
            hint="Every authenticator app accepts a typed secret. Time-based, 6 digits, 30 seconds."
          >
            {({ id }) => (
              <code id={id} className="secret-block">
                {secret.replace(/(.{4})/g, '$1 ').trim()}
              </code>
            )}
          </Field>
        </>
      )}

      <Field label="Code from the app">
        {({ id }) => (
          <Input
            id={id}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Type the 6-digit code"
            className="signin-code"
            value={code}
            onChange={(e) => onCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        )}
      </Field>

      {inlineError && (
        <div className="notice" role="alert">
          <Icon name="warning-circle" />
          <div>{inlineError}</div>
        </div>
      )}

      <Button variant="primary" block type="submit" busy={busy} disabled={!uri}>
        Confirm and sign in
      </Button>

      <div className="row signin-countdown">
        <span className="text-subtle">
          {seconds > 0
            ? `This enrolment expires in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
            : 'This enrolment expired — start again.'}
        </span>
        <button type="button" className="btn btn-ghost header-spacer" onClick={onRestart}>
          Start again
        </button>
      </div>
    </form>
  );
}
